import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { 
  Camera, Upload, DollarSign, Tag, Package, CheckCircle, 
  Gavel, Users, Timer, Trophy, Crown, Play, Sparkles,
  ChevronRight, ChevronDown, Scan, CreditCard, Truck,
  Star, Shield, Zap, Video, ArrowRight, Lock
} from 'lucide-react';

const StepCard = ({ number, icon: Icon, title, description, tips }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-colors">
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#00BFFF]/20 flex items-center justify-center">
        <span className="text-[#00BFFF] font-bold text-lg">{number}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-5 h-5 text-[#00BFFF]" />
          <h3 className="text-white font-semibold text-lg">{title}</h3>
        </div>
        <p className="text-zinc-400 mb-3">{description}</p>
        {tips && (
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <p className="text-sm text-zinc-300">
              <span className="text-[#00BFFF] font-medium">Pro Tip:</span> {tips}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const FeatureHighlight = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 p-4 bg-zinc-800/30 rounded-lg">
    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
      <Icon className="w-5 h-5 text-emerald-400" />
    </div>
    <div>
      <h4 className="text-white font-medium mb-1">{title}</h4>
      <p className="text-zinc-400 text-sm">{description}</p>
    </div>
  </div>
);

const SellersHubPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [activeTab, setActiveTab] = useState('listing');
  
  const isElite = user?.membership_status === 'Elite' || 
                  user?.membership_status === 'Creator' || 
                  user?.can_host_live_auctions;

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00BFFF]/10 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 py-16 relative">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full mb-6">
              <DollarSign className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-medium">0% Seller Fees</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Seller's Hub
            </h1>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              Everything you need to know about selling on Slab Society. 
              List your cards, reach collectors, and keep 100% of your sales.
            </p>
          </div>

          {/* Quick Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Link to="/scanner">
              <Button className="bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black font-semibold px-6 py-3 h-auto">
                <Scan className="w-5 h-5 mr-2" />
                Start Listing Now
              </Button>
            </Link>
            {isElite && (
              <Link to="/auctions">
                <Button className="bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 h-auto">
                  <Gavel className="w-5 h-5 mr-2" />
                  Go Live
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex border-b border-zinc-800 mb-8">
          <button
            onClick={() => setActiveTab('listing')}
            className={`px-6 py-4 font-medium transition-colors relative ${
              activeTab === 'listing' 
                ? 'text-[#00BFFF]' 
                : 'text-zinc-400 hover:text-white'
            }`}
            data-testid="tab-listing"
          >
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              How to List Cards
            </div>
            {activeTab === 'listing' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00BFFF]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('auctions')}
            className={`px-6 py-4 font-medium transition-colors relative ${
              activeTab === 'auctions' 
                ? 'text-amber-400' 
                : 'text-zinc-400 hover:text-white'
            }`}
            data-testid="tab-auctions"
          >
            <div className="flex items-center gap-2">
              <Gavel className="w-5 h-5" />
              Live Auctions
              <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                ELITE
              </span>
            </div>
            {activeTab === 'auctions' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-400" />
            )}
          </button>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        
        {/* ==================== LISTING TUTORIAL ==================== */}
        {activeTab === 'listing' && (
          <div className="space-y-12">
            {/* Benefits Banner */}
            <div className="grid md:grid-cols-3 gap-4">
              <FeatureHighlight 
                icon={DollarSign}
                title="Zero Fees"
                description="Keep 100% of your sale price. No listing fees, no final value fees."
              />
              <FeatureHighlight 
                icon={Scan}
                title="AI-Powered Scanner"
                description="Our AI identifies your card instantly. Just snap a photo."
              />
              <FeatureHighlight 
                icon={Truck}
                title="Easy Shipping"
                description="Pre-calculated rates and one-click label printing."
              />
            </div>

            {/* Step by Step Guide */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Camera className="w-7 h-7 text-[#00BFFF]" />
                Step-by-Step: List Your First Card
              </h2>
              
              <div className="space-y-4">
                <StepCard 
                  number="1"
                  icon={Camera}
                  title="Capture Your Card"
                  description="Go to the AI Scanner page and either use your camera to take a photo or upload an existing image of your card. Make sure the card is well-lit and the entire front is visible."
                  tips="Use natural lighting and avoid glare on slabbed cards. A plain background helps our AI identify the card faster."
                />
                
                <StepCard 
                  number="2"
                  icon={Sparkles}
                  title="AI Identifies Your Card"
                  description="Our AI will automatically detect the player name, set, year, and grade from your image. Review the details and make any corrections if needed."
                  tips="If the AI doesn't recognize your card, you can manually enter the details or search our database for suggestions."
                />
                
                <StepCard 
                  number="3"
                  icon={Tag}
                  title="Set Your Price"
                  description="Enter your asking price. You'll see a breakdown showing your exact payout - remember, you keep 100% with zero seller fees!"
                  tips="Check recent sales of similar cards in the marketplace to price competitively."
                />
                
                <StepCard 
                  number="4"
                  icon={CheckCircle}
                  title="Publish Your Listing"
                  description="Review your listing details and hit 'List Card'. Your card will be live on the marketplace immediately and visible to thousands of collectors."
                  tips="Add detailed descriptions and multiple photos to increase buyer confidence."
                />
              </div>
            </div>

            {/* When You Make a Sale */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-transparent border border-emerald-500/30 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <CreditCard className="w-7 h-7 text-emerald-400" />
                When You Make a Sale
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-sm">1</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">You Get Notified</h4>
                      <p className="text-zinc-400 text-sm">Receive an email the moment someone purchases your card.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-sm">2</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Print Your Label</h4>
                      <p className="text-zinc-400 text-sm">Go to "My Listings" and click "Print Label" - shipping is pre-paid by the buyer.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-sm">3</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Ship Within 3 Days</h4>
                      <p className="text-zinc-400 text-sm">Package your card securely and drop it off at USPS.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-sm">4</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Get Paid</h4>
                      <p className="text-zinc-400 text-sm">Funds are released to your account once the buyer confirms delivery.</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-zinc-900 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4">Example Sale Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between text-zinc-300">
                      <span>Your Price</span>
                      <span>$150.00</span>
                    </div>
                    <div className="flex justify-between text-zinc-400">
                      <span>Platform Fee</span>
                      <span className="text-emerald-400">$0.00</span>
                    </div>
                    <div className="flex justify-between text-zinc-400">
                      <span>Shipping (Paid by Buyer)</span>
                      <span>$5.28</span>
                    </div>
                    <div className="border-t border-zinc-700 pt-3 flex justify-between">
                      <span className="text-white font-semibold">You Receive</span>
                      <span className="text-emerald-400 font-bold text-xl">$150.00</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <Link to="/scanner">
                <Button className="bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black font-bold px-8 py-4 h-auto text-lg">
                  <Scan className="w-6 h-6 mr-2" />
                  List Your First Card
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* ==================== LIVE AUCTIONS TUTORIAL ==================== */}
        {activeTab === 'auctions' && (
          <div className="space-y-12">
            
            {/* Elite Badge Check - Coming Soon */}
            {!isElite && (
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-8 text-center">
                <Sparkles className="w-12 h-12 text-cyan-400/50 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-zinc-400 mb-2 flex items-center justify-center gap-2">
                  Elite Feature
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-zinc-700 text-zinc-400">COMING SOON</span>
                </h3>
                <p className="text-zinc-500 mb-6 max-w-lg mx-auto">
                  Live Auctions will be exclusive to Elite members. We're currently building dedicated 
                  servers for real-time streaming. Stay tuned for updates!
                </p>
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-zinc-700/50 border border-zinc-600">
                  <Sparkles className="w-5 h-5 text-zinc-500" />
                  <span className="text-zinc-400 font-medium">Live Auctions Coming Soon</span>
                </div>
              </div>
            )}

            {/* Benefits Banner */}
            <div className="grid md:grid-cols-3 gap-4">
              <FeatureHighlight 
                icon={Users}
                title="Live Engagement"
                description="Connect with collectors in real-time. Build your audience."
              />
              <FeatureHighlight 
                icon={Timer}
                title="Sudden Death"
                description="Last-second bids reset the timer. Maximum excitement."
              />
              <FeatureHighlight 
                icon={Trophy}
                title="Instant Sales"
                description="Winner pays immediately. No waiting for offers."
              />
            </div>

            {/* What is a Live Auction */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Gavel className="w-7 h-7 text-amber-400" />
                What is a Live Auction?
              </h2>
              <p className="text-zinc-300 text-lg leading-relaxed mb-6">
                Live Auctions let you sell cards in real-time with competitive bidding. 
                Think Whatnot or eBay Live - collectors bid against each other while a 
                countdown timer creates urgency. When the timer hits zero, the highest 
                bidder wins instantly.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg">
                  <Timer className="w-5 h-5 text-[#00BFFF]" />
                  <span className="text-zinc-300">Real-time countdown</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg">
                  <Zap className="w-5 h-5 text-amber-400" />
                  <span className="text-zinc-300">Sudden death timer</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg">
                  <Video className="w-5 h-5 text-red-400" />
                  <span className="text-zinc-300">Stream integration</span>
                </div>
              </div>
            </div>

            {/* Step by Step Guide */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Play className="w-7 h-7 text-amber-400" />
                How to Host a Live Auction
              </h2>
              
              <div className="space-y-4">
                <StepCard 
                  number="1"
                  icon={Tag}
                  title="Have an Active Listing"
                  description="First, you need a card listed on the marketplace. Go to the AI Scanner to list a card if you haven't already. The card must be in 'Active' status to be auctioned."
                  tips="Choose cards that generate excitement - rare pulls, popular players, or high-grade slabs work best for auctions."
                />
                
                <StepCard 
                  number="2"
                  icon={Gavel}
                  title="Start Your Auction"
                  description="Go to the Live Auctions page and click 'Create Auction'. Select your card, set a starting bid, bid increment, and auction duration (1-10 minutes recommended)."
                  tips="Start with a lower opening bid to attract more bidders. Competition drives higher final prices!"
                />
                
                <StepCard 
                  number="3"
                  icon={Video}
                  title="(Optional) Add a Live Stream"
                  description="Paste your Twitch or YouTube Live stream URL to embed video alongside your auction. This lets you interact with bidders in real-time and showcase the card."
                  tips="Engage with your audience! Answer questions about the card, share its story, and build hype."
                />
                
                <StepCard 
                  number="4"
                  icon={Timer}
                  title="Watch the Bidding War"
                  description="Once live, collectors can place bids. The 'Sudden Death' feature means any bid in the last 10 seconds resets the timer to 10 seconds - ensuring the highest bidder truly wins."
                  tips="Promote your auction on social media before going live to maximize attendance."
                />
                
                <StepCard 
                  number="5"
                  icon={Trophy}
                  title="Winner Pays & You Ship"
                  description="When the timer ends, the winner sees a checkout modal with shipping costs included. Once they pay, you'll receive a sale notification. Print the label and ship!"
                  tips="Ship quickly to build your seller reputation and get great reviews."
                />
              </div>
            </div>

            {/* Sudden Death Explanation */}
            <div className="bg-gradient-to-r from-red-500/10 to-amber-500/10 border border-amber-500/30 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Zap className="w-7 h-7 text-amber-400" />
                Understanding Sudden Death
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-zinc-300 mb-4">
                    The <span className="text-amber-400 font-semibold">Sudden Death</span> feature 
                    prevents auction sniping. Here's how it works:
                  </p>
                  <ul className="space-y-3 text-zinc-300">
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span>If someone bids with <strong>10 seconds or less</strong> remaining...</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span>The timer <strong>resets to 10 seconds</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span>This continues until no one bids in the final 10 seconds</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span>The true highest bidder always wins!</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-zinc-900 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4">Example Scenario</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3 p-2 bg-zinc-800 rounded">
                      <Timer className="w-4 h-4 text-zinc-400" />
                      <span className="text-zinc-300">Timer at <span className="text-amber-400">0:08</span></span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-amber-500/20 rounded">
                      <Gavel className="w-4 h-4 text-amber-400" />
                      <span className="text-zinc-300">User places bid of $50</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-emerald-500/20 rounded">
                      <Zap className="w-4 h-4 text-emerald-400" />
                      <span className="text-zinc-300">Timer resets to <span className="text-emerald-400">0:10</span></span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-zinc-800 rounded">
                      <Timer className="w-4 h-4 text-zinc-400" />
                      <span className="text-zinc-300">Countdown continues...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Auction Pricing */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <DollarSign className="w-7 h-7 text-emerald-400" />
                Auction Pricing & Fees
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-white font-semibold mb-4">What the Winner Pays:</h4>
                  <div className="space-y-2 text-zinc-300">
                    <div className="flex justify-between p-2 bg-zinc-800 rounded">
                      <span>Winning Bid</span>
                      <span>$XX.XX</span>
                    </div>
                    <div className="flex justify-between p-2 bg-zinc-800 rounded">
                      <span>Shipping (USPS Ground)</span>
                      <span>~$5.20</span>
                    </div>
                    <div className="flex justify-between p-2 bg-zinc-800 rounded">
                      <span>Processing Fee (3.5%)</span>
                      <span>$X.XX</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-4">What You Receive:</h4>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-6 text-center">
                    <p className="text-zinc-400 mb-2">Your Payout</p>
                    <p className="text-3xl font-bold text-emerald-400">100% of Winning Bid</p>
                    <p className="text-zinc-500 text-sm mt-2">Zero platform fees on auctions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            {isElite ? (
              <div className="text-center">
                <Link to="/auctions">
                  <Button className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-4 h-auto text-lg">
                    <Gavel className="w-6 h-6 mr-2" />
                    Start Your First Auction
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="text-center">
                <Link to="/membership">
                  <Button className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-4 h-auto text-lg">
                    <Crown className="w-6 h-6 mr-2" />
                    Upgrade to Elite
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-16 pt-16 border-t border-zinc-800">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h4 className="text-white font-semibold mb-2">How long until I get paid?</h4>
              <p className="text-zinc-400 text-sm">
                Funds are released once the buyer confirms delivery, typically 3-5 days after shipping.
              </p>
            </div>
            
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h4 className="text-white font-semibold mb-2">What shipping supplies do I need?</h4>
              <p className="text-zinc-400 text-sm">
                For slabbed cards, use a bubble mailer or small box. We recommend adding cardboard for extra protection.
              </p>
            </div>
            
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h4 className="text-white font-semibold mb-2">Can I cancel a listing?</h4>
              <p className="text-zinc-400 text-sm">
                Yes! Go to "My Listings" and click the delete button on any unsold card.
              </p>
            </div>
            
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h4 className="text-white font-semibold mb-2">What if the buyer doesn't pay (auction)?</h4>
              <p className="text-zinc-400 text-sm">
                Auction winners have 24 hours to complete payment. If they don't, the card returns to your listings.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#00BFFF]/10 via-transparent to-amber-500/10 border border-zinc-800 rounded-xl p-12">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Start Selling?</h2>
          <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
            Join thousands of collectors who are selling their cards with zero fees. 
            Your collection, your profit.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {!isAuthenticated ? (
              <Link to="/register">
                <Button className="bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black font-semibold px-6 py-3 h-auto">
                  Create Account
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/scanner">
                  <Button className="bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black font-semibold px-6 py-3 h-auto">
                    <Scan className="w-5 h-5 mr-2" />
                    List a Card
                  </Button>
                </Link>
                <Link to="/my-listings">
                  <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800 px-6 py-3 h-auto">
                    <Package className="w-5 h-5 mr-2" />
                    My Listings
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellersHubPage;
